import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { APPLICATION_LINK_PROPS } from "../config/applicationForm";

const FLYER =
  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/98kz2km0_murabbi%20youth%20intake%20form%20image.png";

const YouthMentorship = () => (
  <main data-testid="youth-mentorship-page" className="bg-ink min-h-screen">
    {/* Ambient header */}
    <section className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 50% at 80% 12%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 45% 50% at 8% 92%, rgba(201,169,97,0.05), transparent 70%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-10 lg:pb-12">
        <Link
          to="/consulting"
          data-testid="youth-back-link"
          className="group inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 hover:text-azure-soft transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-500 group-hover:-translate-x-1" />
          Back to Formation &amp; Initiatives
        </Link>

        <div className="mt-8 flex items-center gap-4">
          <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
            01 — Youth Mentorship Initiative
          </span>
          <span className="h-px w-12 bg-azure-soft/50" />
        </div>

        <h1 className="mt-7 font-serif text-white text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.04] tracking-[-0.02em]">
          Murabbi Youth Initiative.
        </h1>
        <p className="mt-6 text-white/65 text-[15px] lg:text-[16px] leading-[1.9] max-w-2xl">
          A mentorship program for middle-school boys that cultivates faith,
          character, and leadership through meaningful relationships, reflective
          learning, and purposeful experiences.
        </p>
      </div>
    </section>

    {/* First Cohort Applications Now Open */}
    <section className="relative">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 pb-10 lg:pb-14">
        <div
          data-testid="first-cohort-section"
          className="relative overflow-hidden border border-azure/40 bg-ink-soft/50"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 80% 120% at 100% 0%, rgba(74,143,229,0.14), transparent 65%)",
            }}
          />
          <div className="relative p-8 sm:p-10 lg:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.34em] uppercase text-gold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold/60 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                </span>
                Now Accepting Parent Intake Forms
              </div>
              <h2 className="mt-5 font-serif text-white text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.1] tracking-[-0.015em]">
                First Cohort Applications{" "}
                <span className="italic text-azure-soft">Now Open</span>.
              </h2>
              <p className="mt-5 text-white/70 text-[15px] leading-[1.9] max-w-xl">
                The inaugural cohort of the Murabbi Youth Initiative is opening
                its doors. Spaces are intentionally limited so every boy is seen,
                mentored, and known by name.
              </p>
            </div>
            <a
              {...APPLICATION_LINK_PROPS}
              data-testid="first-cohort-apply-btn"
              className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-8 py-4 text-[11px] tracking-[0.32em] uppercase font-medium transition-all whitespace-nowrap"
            >
              Apply for Cohort 1
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Flyer */}
    <section className="relative">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <div className="relative border border-white/10 bg-ink-soft/40 overflow-hidden">
          <img
            src={FLYER}
            alt="Murabbi Youth Initiative — Guiding Young Hearts. Building Strong Character. Developing Future Leaders."
            data-testid="youth-flyer-image"
            className="block w-full h-auto"
          />
        </div>
      </div>
    </section>

    {/* CTA strip */}
    <section className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(74,143,229,0.10), transparent 65%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-22 text-center">
        <h2 className="font-serif text-white text-[24px] lg:text-[30px] leading-[1.2] tracking-[-0.012em]">
          Ready to take the{" "}
          <span className="italic text-azure-soft">next step?</span>
        </h2>
        <p className="mt-5 text-white/65 text-[15px] leading-[1.9] max-w-xl mx-auto">
          Complete the Parent Intake Form to begin the application process for
          the next Murabbi Youth Initiative cohort.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            {...APPLICATION_LINK_PROPS}
            data-testid="youth-apply-cta"
            className="group inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
          >
            Apply for the Next Cohort
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <Link
            to="/consulting"
            data-testid="youth-back-cta"
            className="group inline-flex items-center justify-center gap-3 border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
          >
            All Initiatives
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </div>
    </section>
  </main>
);

export default YouthMentorship;
